import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { StatCard } from '../ui/StatCard';
import { SkeletonRows } from '../ui/Skeleton';
import {
  buildSlate,
  authoritativeCount,
  MIN_VENUES,
  RATER_FLOOR,
  type Candidate,
  type Slate,
} from '../lib/collectionCandidates';
import { loadPreviewCities, readStoredPreviewCity, writeStoredPreviewCity, type PreviewCity } from '../lib/guidePreviewCities';

/**
 * The collections assistant — propose a new rail, see what it would actually contain, approve it.
 *
 * Evan approved this 2026-08-23, with one condition attached: "an assistant that proposes
 * collections returning 3 venues is worse than no assistant."
 *
 * ─── THE COUNT COMES FIRST, AND THAT REORDERS EVERYTHING ─────────────────────────────────────
 * The obvious build is: model proposes -> we count -> Evan approves. That build can waste his time
 * on an idea the corpus cannot fill, and it puts the model in charge of the shortlist. This one is
 * inverted. `buildSlate` enumerates EVERY rule that fills at least `MIN_VENUES` in the selected
 * city and counts each one before anything is sent anywhere. The model is handed that pre-counted
 * slate and does only what it is actually good at: judging which fills are worth shipping and
 * naming them. It cannot return a candidate that is not on the slate — the edge function drops
 * anything that does not resolve.
 *
 * A consequence worth stating: the SLATE ALONE IS USEFUL, and it costs nothing. "Ask Claude" is a
 * second button, never a prerequisite. On a corpus this size the enumeration is often the whole
 * answer — New Orleans, measured 2026-08-24: 34 photo-bearing rated venues, 21 clearing the
 * 2-rater floor, and exactly three tags (indoor 7, chill 4, outdoor 4) plus one category
 * (Night Club, 4) that can fill a rail at all.
 *
 * ─── APPROVE IS TWO STEPS, ON PURPOSE ────────────────────────────────────────────────────────
 * "Create as draft" writes an INACTIVE row through the existing `admin_upsert_search_guide`, then
 * immediately reads `admin_list_search_guides(city).venue_count` back — production's own count,
 * computed by `search_guide_venue_ids`, the same call that fills the rail. Only then does the
 * "Set live" button appear. So the number Evan approves against is never this dashboard's
 * estimate; the estimate exists to keep the shortlist honest, and the audit is what he signs off.
 *
 * An inactive row is invisible to every user by construction — `get_search_guides` selects
 * `WHERE is_active`. Twenty such rows already exist. Drafts are written at `sort_order`
 * DRAFT_SORT_ORDER so they sort to the bottom of the Collections table and are recognisable.
 *
 * ⚠️ NOTHING HERE WRITES ON THE MODEL'S BEHALF. The edge function has no write path at all. Every
 * write on this panel is one of Evan's clicks calling an `admin_*` SECURITY DEFINER RPC that
 * re-validates the spec server-side and is bounded by the `search_guides_kind_check` constraint.
 *
 * ⚠️ THE EDGE FUNCTION IS NOT DEPLOYED. Until `supabase functions deploy collections-assistant`
 * runs and `app_config.collections_assistant` is set to `{"enabled":true,"monthly_cap_usd":5}`,
 * "Ask Claude" reports the error in place and the slate keeps working. Same degrade shape as the
 * rest of this page.
 */

/** Drafts land at the bottom of the Collections table rather than in the middle of the live set. */
const DRAFT_SORT_ORDER = 9000;

const KIND_LABELS: Record<string, string> = {
  tag: 'Thought tag',
  category: 'Category',
  criterion: 'Criterion',
};

type Proposal = {
  candidate_id: string;
  title: string;
  subtitle: string | null;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  kind: string;
  tag: string | null;
  categories: string[] | null;
  predicate: string;
  venue_count: number;
  cities_clearing: number;
  by_city: { city: string; n: number }[];
  already_exists: boolean;
  existing_title: string | null;
  existing_active: boolean;
};

type AssistantReply = {
  assessment: string;
  proposals: Proposal[];
  skipped: { candidate_id: string; label: string; why: string }[];
  model_called?: boolean;
  usage?: { cost_usd: number; month_cost_usd: number; monthly_cap_usd: number };
  error?: string;
};

/** Per-proposal state once Evan has acted on it. */
type Materialised = {
  guideId: string;
  /** Production's own count, from `admin_list_search_guides`. Null while it is being read. */
  trueCount: number | null;
  live: boolean;
};

function errText(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

/** Unwraps the JSON body Supabase Functions hides behind `error.context` on a non-2xx. */
async function callAssistant(body: Record<string, unknown>): Promise<{ ok: true; data: AssistantReply } | { ok: false; error: string }> {
  const { data, error } = await supabase.functions.invoke('collections-assistant', { body });
  if (error) {
    let msg = error.message || 'Request failed';
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string };
        if (parsed?.error) msg = parsed.error;
      } catch {
        /* keep the default message */
      }
    }
    return { ok: false, error: msg };
  }
  const reply = (data ?? {}) as AssistantReply;
  if (reply.error) return { ok: false, error: reply.error };
  return { ok: true, data: reply };
}

/** "New York 12, Dallas 6" — the reach line under a count. */
function reachLine(byCity: { city: string; n: number }[]): string {
  if (byCity.length === 0) return 'reach not measured';
  return byCity.slice(0, 5).map((r) => `${r.city} ${r.n}`).join(' · ');
}

function CountBadge({ n, label }: { n: number; label: string }) {
  if (n < 0) return <span className="admin-badge admin-badge-neutral">count not measured</span>;
  const tone = n >= MIN_VENUES + 3 ? 'admin-badge-accent' : n >= MIN_VENUES ? 'admin-badge-warning' : 'admin-badge-danger';
  return (
    <span className={`admin-badge ${tone}`}>
      {n} {n === 1 ? 'venue' : 'venues'} {label}
    </span>
  );
}

export function CollectionAssistantPanel() {
  const [cities, setCities] = useState<PreviewCity[]>([]);
  const [cityId, setCityId] = useState<string>(readStoredPreviewCity);
  const [slate, setSlate] = useState<Slate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [brief, setBrief] = useState('');
  const [asking, setAsking] = useState(false);
  const [reply, setReply] = useState<AssistantReply | null>(null);
  const [assistantError, setAssistantError] = useState<string | null>(null);

  const [made, setMade] = useState<Record<string, Materialised>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showRejected, setShowRejected] = useState(false);

  const { showError, showSuccess, toastNode } = useToast();

  const city = useMemo(() => cities.find((c) => c.id === cityId) ?? cities[0] ?? null, [cities, cityId]);

  /* `target` is a PARAMETER, not captured state, so the Refresh button keeps a stable identity
     instead of pinning itself to whichever city was selected at first render. */
  const run = useCallback(async (target: PreviewCity | null, all: PreviewCity[]) => {
    setLoading(true);
    setError(null);
    try {
      const built = await buildSlate(
        target ? { id: target.id, name: target.name, pool: target.pool } : null,
        all.map((c) => ({ id: c.id, name: c.name, pool: c.pool })),
      );
      setSlate(built);
      /* A new city invalidates every count on screen — an old proposal counted for New Orleans
         must not sit under a New York header. */
      setReply(null);
      setMade({});
      setAssistantError(null);
    } catch (e) {
      setError(errText(e, 'Could not build the candidate list'));
      setSlate(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await loadPreviewCities();
        if (cancelled) return;
        setCities(list);
        const chosen = list.find((c) => c.id === readStoredPreviewCity()) ?? list[0] ?? null;
        if (chosen) setCityId(chosen.id);
        await run(chosen, list);
      } catch (e) {
        if (!cancelled) {
          setError(errText(e, 'Could not load cities'));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [run]);

  const pickCity = useCallback(
    (id: string) => {
      setCityId(id);
      writeStoredPreviewCity(id);
      void run(cities.find((c) => c.id === id) ?? null, cities);
    },
    [cities, run],
  );

  const ask = useCallback(async () => {
    if (!slate) return;
    setAsking(true);
    setAssistantError(null);
    try {
      /* EVERY existing rail, not just the ones that happen to be candidates — fourteen of the
         sixteen kinds are singletons and never appear on the slate, so filtering to candidates
         would hide "Underground", "Still going at 2am" and the rest from the model and invite a
         duplicate title. */
      const liveList = slate.liveRails
        .filter((r) => r.title)
        .map((r) => ({ title: r.title, kind: r.kind, is_active: r.isActive, renders: r.renders, venue_count: r.venueCount }));
      const res = await callAssistant({
        city: slate.city?.name ?? null,
        brief,
        corpus: {
          city_pool: slate.cityPool,
          city_pool_floored: slate.cityPoolFloored,
        },
        live: liveList,
        /* Only the ones that are not already a row — proposing an existing rail is noise. */
        candidates: slate.candidates.filter((c) => !c.alreadyExists),
      });
      if (!res.ok) {
        setAssistantError(res.error);
        setReply(null);
        return;
      }
      setReply(res.data);
    } finally {
      setAsking(false);
    }
  }, [brief, slate]);

  /**
   * Create the collection as an INACTIVE draft, then read its real count back.
   *
   * `admin_upsert_search_guide` re-validates kind/tag/categories server-side — a `tag` guide with
   * no tag is refused there, not here. `p_id: null` inserts.
   */
  const materialise = useCallback(
    async (p: Proposal) => {
      setBusyId(p.candidate_id);
      try {
        const { data, error: upErr } = await supabase.rpc('admin_upsert_search_guide', {
          p_id: null,
          p_title: p.title,
          p_subtitle: p.subtitle,
          p_kind: p.kind,
          p_city_id: null /* city_id NULL on a city-scoped kind = follow the READER's city */,
          p_tag: p.tag,
          p_categories: p.categories,
          p_venue_ids: null,
          p_sort_order: DRAFT_SORT_ORDER,
          p_is_active: false,
        });
        if (upErr) throw new Error(upErr.message);
        const guideId = typeof data === 'string' ? data : String((data as { id?: string } | null)?.id ?? '');
        if (!guideId) throw new Error('The collection was created but its id was not returned.');

        setMade((m) => ({ ...m, [p.candidate_id]: { guideId, trueCount: null, live: false } }));
        const audit = await authoritativeCount(guideId, slate?.city?.id ?? null);
        setMade((m) => ({
          ...m,
          [p.candidate_id]: { guideId, trueCount: audit?.count ?? null, live: false },
        }));
        showSuccess('Draft created — off, and counted against production.');
      } catch (e) {
        showError(errText(e, 'Could not create the draft'));
      } finally {
        setBusyId(null);
      }
    },
    [showError, showSuccess, slate],
  );

  const setLive = useCallback(
    async (p: Proposal) => {
      const m = made[p.candidate_id];
      if (!m) return;
      setBusyId(p.candidate_id);
      try {
        const { error: actErr } = await supabase.rpc('admin_set_search_guide_active', {
          p_id: m.guideId,
          p_is_active: true,
        });
        if (actErr) throw new Error(actErr.message);
        setMade((prev) => ({ ...prev, [p.candidate_id]: { ...m, live: true } }));
        showSuccess('Live. It is on the Search tab now.');
      } catch (e) {
        showError(errText(e, 'Could not set it live'));
      } finally {
        setBusyId(null);
      }
    },
    [made, showError, showSuccess],
  );

  const freshCandidates = useMemo(
    () => (slate?.candidates ?? []).filter((c) => !c.alreadyExists && c.cityCount >= MIN_VENUES),
    [slate],
  );

  return (
    <div className="admin-page max-w-6xl mx-auto">
      {toastNode}

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="admin-label">City</span>
        <select className="admin-input w-56" value={city?.id ?? ''} onChange={(e) => pickCity(e.target.value)}>
          {cities.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.pool > 0 ? ` · ${c.pool}` : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="admin-btn admin-btn-secondary admin-btn-sm"
          onClick={() => void run(city, cities)}
          disabled={loading}
        >
          {loading ? 'Counting…' : 'Refresh'}
        </button>
      </div>

      {error ? <ErrorBanner className="mb-6">{error}</ErrorBanner> : null}

      {loading ? (
        <SkeletonRows rows={5} />
      ) : !slate ? null : (
        <>
          {/* ── What this city can actually support. Nothing below this is a model opinion. ── */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Rated + photographed here" value={slate.cityPool} sub="can appear on a rail at all" />
            <StatCard
              label={`Clearing the ${RATER_FLOOR}-rater floor`}
              value={slate.cityPoolFloored}
              sub="the real denominator"
            />
            <StatCard label="Rules that fill" value={freshCandidates.length} sub={`≥ ${MIN_VENUES} venues, not already a rail`} />
            <StatCard label="Rules that do not" value={slate.rejected.length} sub="measured and under the minimum" />
          </div>

          {freshCandidates.length === 0 ? (
            <EmptyState
              title="This city cannot support a new collection right now"
              hint={`Every thought tag and category was measured against production and none reaches ${MIN_VENUES} venues here. More rated venues with photos is the only fix — there is no wording of the rule that changes it.`}
            />
          ) : (
            <>
              {/* ── The steer + the one paid button. The slate above was free. ── */}
              <div className="admin-card mb-6 p-4">
                <div className="admin-section-label mb-2">Ask Claude which of these deserve to ship</div>
                <p className="mb-3 text-[12px] leading-relaxed text-mutedDim">
                  {freshCandidates.length} rule{freshCandidates.length === 1 ? '' : 's'} above already fill{' '}
                  {freshCandidates.length === 1 ? 's' : ''} {MIN_VENUES}+ venues in {slate.city?.name ?? 'this city'} and
                  {' '}each count is measured, not guessed. Claude only ranks and names them — it cannot propose a rule
                  that is not on this list, so it cannot propose an empty collection.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    className="admin-input min-w-[16rem] flex-1"
                    placeholder="Optional steer — e.g. 'we have nothing for a low-key weeknight'"
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                  />
                  <button type="button" className="admin-btn admin-btn-primary" onClick={() => void ask()} disabled={asking}>
                    {asking ? 'Thinking…' : 'Ask Claude'}
                  </button>
                </div>
                {assistantError ? <ErrorBanner className="mt-3">{assistantError}</ErrorBanner> : null}
                {reply?.usage ? (
                  <div className="mt-2 text-[11px] text-mutedDim">
                    ${reply.usage.cost_usd.toFixed(3)} this call · ${reply.usage.month_cost_usd.toFixed(2)} of $
                    {reply.usage.monthly_cap_usd} this month
                  </div>
                ) : null}
              </div>

              {reply ? (
                <div className="mb-8">
                  {reply.assessment ? (
                    <p className="mb-4 text-[13px] leading-relaxed text-textPrimary">{reply.assessment}</p>
                  ) : null}

                  {reply.proposals.length === 0 ? (
                    <EmptyState
                      title="Claude proposed nothing"
                      hint="It was shown every rule that fills and judged none of them worth a rail. That is a valid answer on a corpus this size — the candidate table below is still there if you disagree."
                    />
                  ) : (
                    <div className="space-y-3">
                      {reply.proposals.map((p) => {
                        const m = made[p.candidate_id];
                        const busy = busyId === p.candidate_id;
                        return (
                          <div key={p.candidate_id} className="admin-card p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-[15px] font-semibold text-textPrimary">{p.title}</div>
                                {p.subtitle ? (
                                  <div className="mt-0.5 text-[12px] text-muted">{p.subtitle}</div>
                                ) : null}
                                <div className="mt-1 text-[12px] leading-relaxed text-mutedDim">{p.reason}</div>
                              </div>
                              <div className="flex shrink-0 flex-col items-end gap-1">
                                <CountBadge n={p.venue_count} label={`in ${slate.city?.name ?? 'this city'}`} />
                                <span className="admin-badge admin-badge-neutral">{p.confidence} confidence</span>
                              </div>
                            </div>

                            {/* The rule, spelled out. This is the "SQL predicate" half of the ask —
                                a collection is a kind + one parameter, not free-form SQL. */}
                            <div className="mt-3 rounded-md border border-border/80 bg-surface px-3 py-2 font-mono text-[11px] leading-relaxed text-muted">
                              {p.predicate}
                            </div>

                            <div className="mt-2 text-[11px] text-mutedDim">
                              {KIND_LABELS[p.kind] ?? p.kind} · fills in {p.cities_clearing} measured{' '}
                              {p.cities_clearing === 1 ? 'city' : 'cities'} · {reachLine(p.by_city)}
                            </div>

                            {p.venue_count === MIN_VENUES ? (
                              <div className="mt-2 text-[11px] text-amber-300">
                                Sitting exactly on the {MIN_VENUES}-venue minimum — one venue losing its photo removes
                                this rail from the page.
                              </div>
                            ) : null}

                            {/* ── Approve / reject ── */}
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                              {!m ? (
                                <>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-secondary admin-btn-sm"
                                    onClick={() => void materialise(p)}
                                    disabled={busy}
                                  >
                                    {busy ? 'Creating…' : 'Create as draft (off)'}
                                  </button>
                                  <span className="text-[11px] text-mutedDim">
                                    Writes it switched off, then reads the real count back from{' '}
                                    <code className="font-mono">admin_list_search_guides</code>.
                                  </span>
                                </>
                              ) : m.live ? (
                                <span className="admin-status admin-status-success">Live on the Search tab</span>
                              ) : (
                                <>
                                  <span className="admin-status admin-status-neutral">
                                    Draft ·{' '}
                                    {m.trueCount === null
                                      ? 'counting…'
                                      : `production says ${m.trueCount} ${m.trueCount === 1 ? 'venue' : 'venues'}`}
                                  </span>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-primary admin-btn-sm"
                                    onClick={() => void setLive(p)}
                                    disabled={busy || m.trueCount === null || m.trueCount < MIN_VENUES}
                                  >
                                    {busy ? 'Working…' : 'Set live'}
                                  </button>
                                  {m.trueCount !== null && m.trueCount < MIN_VENUES ? (
                                    <span className="text-[11px] text-red-300">
                                      Production disagrees with the estimate — under {MIN_VENUES}, so the app would drop
                                      it. Leave it off.
                                    </span>
                                  ) : (
                                    <span className="text-[11px] text-mutedDim">
                                      Reject by leaving it off — it stays in the Collections table at the bottom.
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {reply.skipped.length > 0 ? (
                    <div className="mt-4">
                      <div className="admin-section-label mb-2">Considered and skipped</div>
                      <ul className="space-y-1 text-[12px] text-mutedDim">
                        {reply.skipped.map((s) => (
                          <li key={s.candidate_id}>
                            <span className="text-textPrimary">{s.label}</span> — {s.why}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* ── The full slate. Present with or without a model call. ── */}
              <div className="admin-section-label mb-2">
                Every rule that fills in {slate.city?.name ?? 'this city'}
              </div>
              <div className="overflow-x-auto">
                <table className="admin-table admin-table--compact">
                  <thead className="admin-table-head">
                    <tr>
                      <th>Rule</th>
                      <th>Kind</th>
                      <th className="text-right">Here</th>
                      <th className="text-right">Cities</th>
                      <th>Reach</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slate.candidates.map((c: Candidate) => (
                      <tr key={c.id}>
                        <td className="font-mono text-[11px]">{c.tag ?? c.categories?.[0] ?? c.id}</td>
                        <td>{KIND_LABELS[c.kind] ?? c.kind}</td>
                        <td className="text-right tabular-nums">{c.cityCount < 0 ? '—' : c.cityCount}</td>
                        <td className="text-right tabular-nums">{c.citiesClearing}</td>
                        <td className="text-[11px] text-mutedDim">{reachLine(c.byCity)}</td>
                        <td>
                          {c.alreadyExists ? (
                            <span className={`admin-badge ${c.existingActive ? 'admin-badge-accent' : 'admin-badge-neutral'}`}>
                              {c.existingActive ? 'live' : 'off'} · {c.existingTitle}
                            </span>
                          ) : (
                            <span className="admin-badge admin-badge-warning">available</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ── Why the list is short. This is the honest half and it is worth showing. ── */}
          {slate.rejected.length > 0 ? (
            <div className="mt-6">
              <button
                type="button"
                className="admin-btn admin-btn-ghost admin-btn-sm"
                onClick={() => setShowRejected((v) => !v)}
              >
                {showRejected ? 'Hide' : 'Show'} the {slate.rejected.length} rules that do not reach {MIN_VENUES} here
              </button>
              {showRejected ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {slate.rejected.map((r) => (
                    <span key={r.id} className="admin-badge admin-badge-neutral">
                      {r.label} · {r.cityCount}
                    </span>
                  ))}
                </div>
              ) : null}
              <p className="mt-3 max-w-2xl text-[11px] leading-relaxed text-mutedDim">
                Counts on this page other than the draft audit are computed in the browser from{' '}
                <code className="font-mono">venues</code> + <code className="font-mono">logs</code>, replicating the
                pool, the {RATER_FLOOR}-rater floor and the tag/category membership arms of{' '}
                <code className="font-mono">search_guide_venue_ids</code>. Verified against production on 2026-08-24 for
                New Orleans: five for five on the tag guides that exist. The authoritative number is always the one
                shown after "Create as draft".
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
